import React, { useEffect, useState } from 'react';
import Heading from '../components/heading/heading';
import axios from '../backendService';
import Cookies from 'js-cookie';
import { PaginationStyle } from '../container/styled';
import { Badge, Button, Select, Skeleton, Space, Table, Tooltip, message } from 'antd';
import * as moment from 'moment-timezone';
import { MdDeleteForever } from 'react-icons/md';
import { FaPoll } from 'react-icons/fa';
import { FaClipboardQuestion } from 'react-icons/fa6';
import { SelectWrapperStyle } from '../container/ui-elements/ui-elements-styled';
import DeleteConfirmation from '../components/video-components/DeleteConfirmation';
import { useNavigate } from 'react-router-dom';

const ScheduledSessionNew = () => {

const userId = Cookies.get('userId');
const navigate = useNavigate();

const [sessions, setSessions] = useState([]);
const [loading, setLoading] = useState(false);
const [assignedCourses, setAssignedCourses] = useState([]);
const [currentCourse, setCurrentCourse] = useState('');
const [videoStatus, setVideoStatus] = useState('ALL');
const [isModalOpen, setIsModalOpen] = useState(false);
const [currentSessionId, setCurrentSessionId] = useState('');
const [currentThumbnailURL, setCurrentThumbnailURL] = useState('');
const [pagination, setPagination] = useState({
  current: 1,
  pageSize: 10,
  total: 0
});

useEffect(() => {
  const initialFetch = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/users/sessions/${userId}`);
      const { videos, courses, pagination: fetchedPagination } = response.data;

      if (courses.length > 0) {
        setAssignedCourses(courses);
        setCurrentCourse(courses[0]._id); 
      }

      setPagination({
        current: 1,
        pageSize: 10,
        total: fetchedPagination.total
      });
    } catch (err) {
      console.error('Error:', err);
      message.error('Failed to fetch initial data');
    } finally {
      setLoading(false);
    }
  };

  if (!currentCourse) initialFetch(); 
}, []);

const fetchData = async (page = 1) => {
  if (!currentCourse) return;  

  setLoading(true);
  try {
    const queryParams = new URLSearchParams({
      courseId: currentCourse,
      status: videoStatus,
      page: page.toString(),
      limit: pagination.pageSize.toString()
    });

    const response = await axios.get(`/users/sessions/${userId}?${queryParams}`);
    const { videos, pagination: fetchedPagination, courses } = response.data;

    if (assignedCourses.length === 0 && courses.length > 0) {
      setAssignedCourses(courses);
      setCurrentCourse(courses[0]._id); 
    }

    setSessions(videos);
    setPagination(prevState => ({
      ...prevState,
      current: page,
      total: fetchedPagination.total
    }));
  } catch (err) {
    console.error('Error:', err);
    message.error('Failed to fetch sessions');
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  if (currentCourse !== '') fetchData();
}, [currentCourse, videoStatus]);  

  const handleQuestionsRedirect = (videoId) => {
    navigate(`${videoId}/video-questions`);
  };

  const handlePollsRedirect = (videoId) => {
    navigate(`${videoId}/video-polls`);
  };

  const handleRoomRedirect = async (roomKey, sessionId) => {
    try {
      const response = await axios.post(`/videos/createSessionWithThumbnailUrls/make-session-live/${sessionId}`);
      if (response.data.message === `Session ${sessionId} is now live`) {
        message.success('Session is now enabled for students');
        setTimeout(() => {
          navigate(`${roomKey}/live-session`);
        }, 800);
      }
    } catch (error) {
      message.error('Cannot enter the room.');
    }
  };

  const handleCourseChange = (value) => {
    setVideoStatus('ALL');
    setCurrentCourse(value);
  };

  const handleDeleteModalOpen = (sessionId, thumbnailURL) => {
    setIsModalOpen(true);
    setCurrentSessionId(sessionId);
    setCurrentThumbnailURL(thumbnailURL);
  };

  const onCancel = () => {
    setIsModalOpen(false);
  };

  const handleTableChange = (pagination) => {
    fetchData(pagination.current);
  };

const columns = [
  {
    title: 'Published Date',
    dataIndex: 'publishDate',
    key: 'publishDate',
    width: 100,
    align: 'center',
    className: 'ltr:pr-4 rtl:pl-4 text-dark dark:text-white87 font-medium text-[15px] py-[16px] last:text-end border-none capitalize before:hidden',
    render: (text) => (
      <Tooltip title={text}>
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            maxWidth: '100px',
          }}
        >
          {text}
        </span>
      </Tooltip>
    ),
  },
  {
    title: 'Subject',
    dataIndex: ['subject', 'subjectName'],
    key: 'subject',
    width: 100,
    className: 'ltr:pr-4 rtl:pl-4 text-dark dark:text-white87 font-medium text-[15px] py-[16px] last:text-end border-none capitalize before:hidden',
    render: (text) => (
      <Tooltip title={text}>
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            maxWidth: '100px',
          }}
        >
          {text}
        </span>
      </Tooltip>
    ),
  },
  {
    title: 'Chapter',
    dataIndex: ['chapter', 'chapterName'],
    key: 'chapter',
    width: 100,
    className: 'ltr:pr-4 rtl:pl-4 text-dark dark:text-white87 font-medium text-[15px] py-[16px] last:text-end border-none capitalize before:hidden',
    render: (text) => (
      <Tooltip title={text}>
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            maxWidth: '100px',
          }}
        >
          {text}
        </span>
      </Tooltip>
    ),
  },
  {
    title: 'Topic',
    dataIndex: ['topic', 'topicName'],
    key: 'topic',
    width: 100,
    className: 'ltr:pr-4 rtl:pl-4 text-dark dark:text-white87 font-medium text-[15px] py-[16px] last:text-end border-none capitalize before:hidden',
    render: (text) => (
      <Tooltip title={text}>
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            maxWidth: '100px',
          }}
        >
          {text}
        </span>
      </Tooltip>
    ),
  },
  {
    title: 'Video title',
    dataIndex: 'title',
    key: 'title',
    width: 100,
    className: 'ltr:pr-4 rtl:pl-4 text-dark dark:text-white87 font-medium text-[15px] py-[16px] last:text-end border-none capitalize before:hidden',
    render: (text) => (
      <Tooltip title={text}>
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            maxWidth: '100px',
          }}
        >
          {text}
        </span>
      </Tooltip>
    ),
  },
  // {
  //   title: 'Polls',
  //   dataIndex: 'polls',
  //   key: 'polls',
  //   width: 100,
  //   align: 'center',
  //   className: 'ltr:pr-4 rtl:pl-4 text-dark dark:text-white87 font-medium text-[15px] py-[16px] last:text-end border-none capitalize before:hidden',
  //   render: (text, record) => (
  //     <div
  //       onClick={() => handlePollsRedirect(record._id)}
  //       className="p-2 rounded-full flex items-center justify-center bg-white cursor-pointer"
  //     >
  //       <FaPoll className="w-4 h-4" color="purple" />
  //     </div>
  //   ),
  // },
  // {
  //   title: 'Questions',
  //   dataIndex: 'questions',
  //   key: 'questions',
  //   width: 100,
  //   align: 'center',
  //   className: 'ltr:pr-4 rtl:pl-4 text-dark dark:text-white87 font-medium text-[15px] py-[16px] last:text-end border-none capitalize before:hidden',
  //   render: (text, record) => {
  //     // Debugging: Console log to verify the data for videoQuestions
  //     console.log("Record for questions column:", record);
  //     console.log("videoQuestions data:", record?.videoQuestions);
  
  //     const unansweredQuestionsCount = record?.videoQuestions
  //       ? record.videoQuestions.filter((item) => item.answer === '').length
  //       : 0;
  
  //     return (
  //       <div
  //         onClick={() => handleQuestionsRedirect(record._id)}
  //         className="p-2 rounded-full flex items-center justify-center bg-white cursor-pointer"
  //       >
  //         <FaClipboardQuestion className="w-4 h-4" color="purple" />
  //         <span className="absolute -top-2 -right-2 left-auto w-full">
  //           <Badge
  //             size={'default'}
  //             className="w-1 h-1"
  //             count={unansweredQuestionsCount}
  //           />
  //         </span>
  //       </div>
  //     );
  //   },
  // },
  
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 100,
    align: 'center',
    className: 'ltr:pr-4 rtl:pl-4 text-dark dark:text-white87 font-medium text-[15px] py-[16px] last:text-end border-none capitalize before:hidden',
    render: (status) => {
      const statusConfig = {
        Ongoing: {
          color: '#4c9923',
          text: 'Ongoing'
        },
        Completed: {
          color: 'blue',
          text: 'Completed'
        },
        Upcoming: {
          color: 'gold',
          text: 'Upcoming'
        },
        Not_assigned: {
          color: '#bbb',
          text: 'Not assigned'
        }
      };

      const config = statusConfig[status] || statusConfig.Not_assigned;

      return (
        <Badge
          className="site-badge-count-109"
          count={config.text}
          style={{
            backgroundColor: config.color,
          }}
        />
      );
    },
  },
  {
    title: 'Action',
    dataIndex: 'action',
    key: 'action',
    width: 100,
    align: 'center',
    className: 'ltr:pr-4 rtl:pl-4 text-dark dark:text-white87 font-medium text-[15px] py-[16px] last:text-end border-none capitalize before:hidden',
    render: (text, record) => (
      <Button
        disabled={record.status !== 'Ongoing'}
        onClick={() => handleRoomRedirect(record.roomKey, record._id)}
        type={'primary'}
        size={'small'}
      >
        Enter the room
      </Button>
    ),
  },
  {
    title: 'Start time',
    dataIndex: 'startTime',
    key: 'startTime',
    width: 100,
    align: 'center',
    className: 'ltr:pr-4 rtl:pl-4 text-dark dark:text-white87 font-medium text-[15px] py-[16px] last:text-end border-none capitalize before:hidden ltr:rounded-r-[10px] rtl:rounded-l-[10px] ltr:pr-[25px] rtl:pl-[25px]',
    render: (text) => (
      <Tooltip title={text}>
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            maxWidth: '100px',
          }}
        >
          {text}
        </span>
      </Tooltip>
    ),
  },
  {
    title: 'End time',
    dataIndex: 'endTime',
    key: 'endTime',
    width: 100,
    align: 'center',
    className: 'ltr:pr-4 rtl:pl-4 text-dark dark:text-white87 font-medium text-[15px] py-[16px] last:text-end border-none capitalize before:hidden ltr:rounded-r-[10px] rtl:rounded-l-[10px] ltr:pr-[25px] rtl:pl-[25px]',
    render: (text) => (
      <Tooltip title={text}>
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            maxWidth: '100px',
          }}
        >
          {text}
        </span>
      </Tooltip>
    ),
  },
  {
    title: 'Delete',
    dataIndex: 'delete',
    key: 'delete',
    width: 100,
    align: 'center',
    className: 'ltr:pr-4 rtl:pl-4 text-dark dark:text-white87 font-medium text-[15px] py-[16px] last:text-end border-none capitalize before:hidden',
    render: (text, record) => (
      <span
        className="flex items-center justify-center cursor-pointer"
        onClick={() => handleDeleteModalOpen(record._id, record.thumbnailURL)}
      >
        <MdDeleteForever className="w-6 h-6 text-primary" />
      </span>
    ),
  },
];

  return (
    <div className="min-h-screen bg-white">
      <div className="flex items-center justify-between flex-wrap gap-[20px] ssm:flex-col pt-[42px] px-[25px] text-dark dark:text-white87 font-medium text-[17px]">
        <Heading as="h4" className="text-dark dark:text-white87 text-[22px] font-semibold mb-0">
          Scheduled sessions
        </Heading>
      </div>
      <div className="inline-flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          {loading && sessions.length === 0 ? (
            <SelectWrapperStyle className="flex items-center gap-x-[15px] gap-y-[10px] p-[25px]">
              <Space size={'large'}>
                <Skeleton.Input active size={'large'} className="w-[150px]" block />
                <Skeleton.Input active size={'large'} className="w-[150px]" block />
              </Space>
            </SelectWrapperStyle>
          ) : (
            <SelectWrapperStyle className="flex items-center gap-x-[15px] gap-y-[10px] p-[25px]">
              <Select
                className="[&>div]:border-normal dark:[&>div]:border-white10 [&>div]:rounded-6 [&>.ant-select-arrow]:text-theme-gray dark:[&>.ant-select-arrow]:text-white60 [&>div>div>div>span]:bg-transparent [&>div]:h-[38px] [&>div>div>div>span]:items-center [&>div>.ant-select-selection-item]:flex [&>div>.ant-select-selection-item]:items-center dark:[&>div>.ant-select-selection-item]:text-white60"
                value={currentCourse}
                placeholder="Select course"
                style={{ width: 240 }}
                onChange={handleCourseChange}
              >
                {}
                {assignedCourses.map((course) => (
                  <Select.Option key={course._id} value={course._id}>
                    {course.courseName}
                  </Select.Option>
                ))}
              </Select>
              {}
              <Select
                className="[&>div]:border-normal dark:[&>div]:border-white10 [&>div]:rounded-6 [&>.ant-select-arrow]:text-theme-gray dark:[&>.ant-select-arrow]:text-white60 [&>div>div>div>span]:bg-transparent [&>div]:h-[38px] [&>div>div>div>span]:items-center [&>div>.ant-select-selection-item]:flex [&>div>.ant-select-selection-item]:items-center dark:[&>div>.ant-select-selection-item]:text-white60"
                value={videoStatus}
                onChange={(v) => setVideoStatus(v)}
                style={{ width: 240 }}
              >
                <Select.Option value="ALL">ALL</Select.Option>
                <Select.Option value="Ongoing">Ongoing</Select.Option>
                <Select.Option value="Upcoming">Upcoming</Select.Option>
                <Select.Option value="Completed">Completed</Select.Option>
                <Select.Option value="Not_assigned">Not assigned</Select.Option>
              </Select>
            </SelectWrapperStyle>
          )}
        </div>
      </div>
      <div className="p-6">
        {loading && sessions.length === 0 ? (
          <Skeleton.Input active size={'large'} className="w-full" block />
        ) : (
          <PaginationStyle>
            <div className="table-pl-0 hover-tr-none table-pt-15 table-responsive ant-pagination-custom-style [&>div>div>div>ul]:pt-[30px] [&>div>div>div>ul]:mt-[20px] [&>div>div>div>ul]:border-normal dark:[&>div>div>div>ul]:border-white10 [&>div>div>div>ul]:border-t-1 [&>div>div>div>ul]:justify-center">
              <Table
                pagination={{
                  ...pagination,
                  showSizeChanger: false
                }}
                onChange={handleTableChange}
                dataSource={sessions}
                columns={columns}
              />
            </div>
          </PaginationStyle>
        )}
      </div>
      <DeleteConfirmation
        isModalOpen={isModalOpen}
        onCancel={onCancel}
        sessionId={currentSessionId}
        userId={userId}
        thumbnailURL={currentThumbnailURL}
        fetchData={fetchData}
      />
    </div>
  );
};

export default ScheduledSessionNew;